import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Package,
  Truck,
  MapPin,
  CreditCard,
  Mail,
  Calendar,
  ArrowLeft,
  RefreshCcw,
  HelpCircle,
  Box,
  ListOrdered,
  Upload,
  X,
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  getOrderById,
  reorderItems,
  requestReturn,
  cancelOrder,
  selectCurrentOrder,
  selectCurrentOrderLoading,
  selectCurrentOrderError,
  clearCurrentOrder,
} from "@/store/slices/orderSlice";
import { fetchProductById } from "@/store/slices/productSlice";
import Loader from "@/components/loader/loader";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/format";

export default function OrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const orderData = useAppSelector(selectCurrentOrder);
  const loading = useAppSelector(selectCurrentOrderLoading);
  const error = useAppSelector(selectCurrentOrderError);

  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Return request state
  const [returnReason, setReturnReason] = useState("");
  const [returnImages, setReturnImages] = useState([]);
  const [selectedReturnItems, setSelectedReturnItems] = useState([]);

  // Fetch order data
  useEffect(() => {
    if (orderId) {
      dispatch(getOrderById(orderId));
    }

    return () => {
      dispatch(clearCurrentOrder());
    };
  }, [orderId, dispatch]);

  // Refresh order data function
  const refreshOrderData = () => {
    if (orderId) {
      dispatch(clearCurrentOrder());
      dispatch(getOrderById(orderId));
    }
  };

  // Transform order data for display
  const order = useMemo(() => {
    if (!orderData) return null;

    // Parse shipping address - handle new API structure
    let shippingAddress = {};
    try {
      if (orderData.shippingAddress) {
        const parsed =
          typeof orderData.shippingAddress === "string"
            ? JSON.parse(orderData.shippingAddress)
            : orderData.shippingAddress;

        // Ensure all fields are strings, not nested objects
        const getStringValue = (value, fallback = "") => {
          if (typeof value === "string") return value;
          if (typeof value === "object" && value !== null) {
            // Handle {value, label} structure
            if (value.label) return value.label;
            if (value.value) return value.value;
          }
          return fallback;
        };

        const firstName = getStringValue(parsed.firstName);
        const lastName = getStringValue(parsed.lastName);
        const fullNameStr =
          firstName && lastName
            ? `${firstName} ${lastName}`
            : firstName || lastName || "";

        shippingAddress = {
          name: getStringValue(
            parsed.name,
            getStringValue(parsed.fullName, fullNameStr),
          ),
          fullName: getStringValue(
            parsed.fullName,
            getStringValue(parsed.name, fullNameStr),
          ),
          firstName: firstName,
          lastName: lastName,
          email: getStringValue(parsed.email),
          phone: getStringValue(parsed.phone),
          address: getStringValue(parsed.address),
          address1: getStringValue(
            parsed.address1,
            getStringValue(parsed.address),
          ),
          address2: getStringValue(parsed.address2),
          city: getStringValue(parsed.city),
          state: getStringValue(parsed.state),
          country: getStringValue(parsed.country),
          zipCode: getStringValue(parsed.zipCode, getStringValue(parsed.zip)),
          zip: getStringValue(parsed.zip, getStringValue(parsed.zipCode)),
          is_pickup: parsed.is_pickup || false,
        };
      } else {
        // Build from individual fields if available
        shippingAddress = {
          name: orderData.shippingName || "",
          phone: orderData.shippingPhone || "",
          email: orderData.shippingEmail || "",
          address1: orderData.shippingAddress1 || "",
          city: orderData.shippingCity || "",
          state: orderData.shippingState || "",
          country: orderData.shippingCountry || "",
          zipCode: orderData.shippingZipCode || orderData.shippingZip || "",
        };
      }
    } catch (err) {
      // If parsing fails, try to extract what we can
      if (
        typeof orderData.shippingAddress === "object" &&
        orderData.shippingAddress !== null
      ) {
        shippingAddress = {
          raw: JSON.stringify(orderData.shippingAddress),
        };
      } else {
        shippingAddress = { raw: String(orderData.shippingAddress || "") };
      }
    }

    // Get first shipment for tracking
    const firstShipment =
      orderData.shipments && orderData.shipments.length > 0
        ? orderData.shipments[0]
        : null;

    return {
      id: orderData.orderId || orderData.order_id || orderData.id,
      date: new Date(
        orderData.createdAt || orderData.created_at,
      ).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      createdAt: orderData.createdAt || orderData.created_at,
      deliveredAt: orderData.deliveredAt || orderData.delivered_at,
      cancelledAt: orderData.cancelledAt || orderData.cancelled_at,
      status: orderData.status,
      subtotal: parseFloat(orderData.subtotal || 0),
      taxAmount: parseFloat(orderData.taxAmount || orderData.tax_amount || 0),
      discountAmount: parseFloat(
        orderData.discountAmount || orderData.discount_amount || 0,
      ),
      shippingCost: parseFloat(
        orderData.shippingCost || orderData.shipping_cost || 0,
      ),
      total: parseFloat(orderData.total || 0),
      paymentMethod:
        orderData.paymentMethod ||
        orderData.payment_method ||
        "Payment method not available",
      paymentStatus: orderData.paymentStatus || orderData.payment_status,
      paymentTransactionId:
        orderData.paymentTransactionId || orderData.payment_transaction_id,
      shippingAddress: shippingAddress,
      billingAddress: shippingAddress, // Using shipping as billing for now
      products: orderData.items || [],
      trackingNumber: firstShipment?.tracking_number || null,
      estimatedDelivery: firstShipment?.estimated_delivery
        ? new Date(firstShipment.estimated_delivery).toLocaleDateString(
            "en-US",
            {
              month: "long",
              day: "numeric",
              year: "numeric",
            },
          )
        : null,
      shipments: orderData.shipments || [],
      history: orderData.history || [],
      notes: orderData.notes,
      metadata: orderData.metadata,
      user: orderData.user,
    };
  }, [orderData]);

  const handleReorder = async () => {
    if (!order || !orderId) return;

    try {
      setIsProcessing(true);
      const result = await dispatch(
        reorderItems({
          orderId,
          reorderData: {
            shippingCost: order.shippingCost,
            paymentMethod: "unknown",
            shippingAddress: order.shippingAddress,
          },
        }),
      ).unwrap();

      setReorderDialogOpen(false);

      // Navigate to new order or orders list
      if (result.orderId) {
        navigate(`/dashboard/orders/${result.orderId}`);
      } else {
        navigate("/dashboard/orders");
      }
    } catch (err) {
      // Error already handled in thunk
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReturnRequest = async () => {
    if (!order || !orderId || !returnReason.trim()) {
      toast.error("Please provide a return reason");
      return;
    }

    if (returnImages.length === 0) {
      toast.error("Please upload at least one product image");
      return;
    }

    try {
      setIsProcessing(true);
      const itemsToReturn =
        selectedReturnItems.length > 0
          ? selectedReturnItems.map((item) => ({
              productId: item.product_id,
              quantity: item.quantity,
              returnReason: returnReason,
            }))
          : undefined;

      await dispatch(
        requestReturn({
          orderId,
          returnData: {
            reason: returnReason,
            itemsToReturn: itemsToReturn,
          },
          images: returnImages,
        }),
      ).unwrap();

      setReturnDialogOpen(false);
      setReturnReason("");
      setReturnImages([]);
      setSelectedReturnItems([]);

      // Refresh order data
      dispatch(getOrderById(orderId));
    } catch (err) {
      // Error already handled in thunk
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + returnImages.length > 10) {
      toast.error("Maximum 10 images allowed");
      return;
    }
    setReturnImages([...returnImages, ...files]);
  };

  const removeImage = (index) => {
    setReturnImages(returnImages.filter((_, i) => i !== index));
  };

  const toggleReturnItem = (item) => {
    const exists = selectedReturnItems.find(
      (i) => i.product_id === item.product_id,
    );
    if (exists) {
      setSelectedReturnItems(
        selectedReturnItems.filter((i) => i.product_id !== item.product_id),
      );
    } else {
      setSelectedReturnItems([...selectedReturnItems, item]);
    }
  };

  const handleCancelOrder = async () => {
    if (
      !order ||
      !orderId ||
      !window.confirm("Are you sure you want to cancel this order?")
    ) {
      return;
    }

    try {
      setIsProcessing(true);
      await dispatch(cancelOrder(orderId)).unwrap();
      // Refresh order data
      dispatch(getOrderById(orderId));
    } catch (err) {
      // Error already handled in thunk
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to safely extract value from address fields (handles both strings and objects)
  const getAddressValue = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      // Check if this is the entire shippingAddress object (has multiple address keys)
      // If it has keys like city, name, email, phone, state, address, country, zipCode, etc.
      // then it's the entire address object, not a single field value
      const addressKeys = [
        "city",
        "name",
        "email",
        "phone",
        "state",
        "address",
        "country",
        "zipCode",
        "fullName",
        "lastName",
        "firstName",
        "address1",
        "address2",
      ];
      const hasMultipleAddressKeys =
        addressKeys.filter((key) => key in field).length > 3;

      if (hasMultipleAddressKeys) {
        // This is the entire address object, not a field value - return empty string
        return "";
      }

      // Handle {value, label} structure
      if (field.label) return field.label;
      if (field.value) return field.value;
      // If it's an object but no label/value, return empty string
      return "";
    }
    return String(field);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-100 text-green-700";
      case "Shipped":
        return "bg-blue-100 text-blue-700";
      case "Processing":
      case "Pending":
        return "bg-yellow-100 text-yellow-700";
      case "Cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Package className="w-16 h-16 mb-4" />
        <p className="text-lg">{error || "Order not found."}</p>
        <Link to="/dashboard/orders">
          <Button variant="link" className="mt-4">
            Back to Orders
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <ListOrdered className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-primary">Order Details</h1>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={refreshOrderData}
            disabled={loading}
            className="flex-1 md:flex-none border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
          >
            <RefreshCcw
              className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Link to="/dashboard/orders" className="flex-1 md:flex-none">
            <Button
              variant="outline"
              className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Orders
            </Button>
          </Link>
        </div>
      </div>
      <p className="text-gray-600">
        Detailed information about your order {order.id}.
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Order Summary & Products */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary break-all">
                Order {order.id}
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{order.date}</span>
                <Separator orientation="vertical" className="h-4" />
                <Badge className={getStatusBadgeColor(order.status)}>
                  {order.status}
                </Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Products List */}
              <div className="space-y-4">
                <h3 className="font-semibold text-primary text-lg">
                  Products in this Order
                </h3>
                {order.products.map((product, index) => (
                  <div
                    key={product.product_id || index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex items-start gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        {product.product_image_url || product.product_image ? (
                          <img
                            src={
                              product.product_image_url || product.product_image
                            }
                            alt={product.product_name || "Product image"}
                            className="w-24 h-24 md:w-32 md:h-32 rounded-lg object-cover shadow-sm border border-gray-200 bg-gray-100"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholder.svg";
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg bg-gray-200 border border-gray-300 flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-primary text-base md:text-lg line-clamp-2">
                              {product.product_name}
                            </h4>

                            {/* Display variation info if present */}
                            {product.variation && (
                              <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                {product.variation.variantName && (
                                  <div className="font-medium">
                                    {product.variation.variantName}
                                  </div>
                                )}
                                <div className="flex gap-2 flex-wrap">
                                  {product.variation.size && (
                                    <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                      Size: {product.variation.size}
                                    </span>
                                  )}
                                  {product.variation.color && (
                                    <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                      Color: {product.variation.color}
                                    </span>
                                  )}
                                  {product.variation.scent && (
                                    <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                      Scent: {product.variation.scent}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                Qty: {product.quantity}
                              </span>
                              <span className="text-sm text-gray-600">
                                Unit Price:{" "}
                                {formatCurrency(
                                  product.final_price ||
                                    product.product_price ||
                                    product.price ||
                                    0,
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            <p className="font-bold text-primary text-lg">
                              {(() => {
                                // Try multiple possible price field names
                                const priceValue =
                                  product.final_price ??
                                  product.product_price ??
                                  product.price ??
                                  product.basePrice ??
                                  product.item_price ??
                                  0;

                                // Parse to number, defaulting to 0 if invalid
                                const numPrice =
                                  typeof priceValue === "number"
                                    ? priceValue
                                    : parseFloat(priceValue) || 0;

                                // Parse quantity, defaulting to 1 if invalid
                                const quantity =
                                  typeof product.quantity === "number"
                                    ? product.quantity
                                    : parseFloat(product.quantity) || 1;

                                // Calculate total (price might already be in base currency, not cents)
                                const total = numPrice * quantity;

                                // Only format if we have a valid number
                                return isNaN(total) || total <= 0
                                  ? formatCurrency(0)
                                  : formatCurrency(total);
                              })()}
                            </p>
                            {product.discount_percent > 0 && (
                              <p className="text-xs text-green-600">
                                {product.discount_percent}% off
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Separator />

              {/* Price Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(order.subtotal)}</span>
                </div>
                {order.discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(order.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Tax</span>
                  <span>{formatCurrency(order.taxAmount)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>
                    {order.shippingCost === 0
                      ? "Free"
                      : formatCurrency(order.shippingCost)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-primary">
                  <span>Grand Total</span>
                  <span>{formatCurrency(order.total)}</span>
                </div>
                {order.paymentStatus && (
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-gray-600">Payment Status:</span>
                    <Badge
                      className={
                        order.paymentStatus === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }
                    >
                      {order.paymentStatus}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tracking Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Shipment Tracking</CardTitle>
              <CardDescription>
                Track the status of your delivery.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {order.trackingNumber ? (
                <>
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-secondary" />
                    <div>
                      <p className="font-medium text-primary break-all">
                        Tracking Number: {order.trackingNumber}
                      </p>
                      {order.estimatedDelivery && (
                        <p className="text-sm text-gray-600">
                          Estimated Delivery: {order.estimatedDelivery}
                        </p>
                      )}
                    </div>
                  </div>
                  <Link to={`/dashboard/orders/track/${order.trackingNumber}`}>
                    <Button className="bg-primary hover:bg-primary/90 text-white">
                      <Truck className="w-4 h-4 mr-2" /> View Tracking Details
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="text-gray-500 flex items-center gap-2">
                  <Box className="w-5 h-5" />
                  <span>No tracking information available yet.</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Addresses & Payment */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">
                {order.shippingAddress.is_pickup
                  ? "Pickup Information"
                  : "Shipping Information"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              {getAddressValue(order.shippingAddress.name) && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <p className="font-medium">
                    {getAddressValue(order.shippingAddress.name)}
                  </p>
                </div>
              )}
              {getAddressValue(order.shippingAddress.address1) && (
                <p className="ml-6">
                  {getAddressValue(order.shippingAddress.address1)}
                </p>
              )}
              {getAddressValue(order.shippingAddress.address2) && (
                <p className="ml-6">
                  {getAddressValue(order.shippingAddress.address2)}
                </p>
              )}
              {(getAddressValue(order.shippingAddress.city) ||
                getAddressValue(order.shippingAddress.state) ||
                getAddressValue(order.shippingAddress.zip)) && (
                <p className="ml-6">
                  {getAddressValue(order.shippingAddress.city)}
                  {getAddressValue(order.shippingAddress.city) &&
                  getAddressValue(order.shippingAddress.state)
                    ? ", "
                    : ""}
                  {getAddressValue(order.shippingAddress.state)}{" "}
                  {getAddressValue(order.shippingAddress.zip)}
                </p>
              )}
              {getAddressValue(order.shippingAddress.country) && (
                <p className="ml-6">
                  {getAddressValue(order.shippingAddress.country)}
                </p>
              )}
              {getAddressValue(order.shippingAddress.phone) && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-secondary" />
                  <p>{getAddressValue(order.shippingAddress.phone)}</p>
                </div>
              )}
              {order.shippingAddress.raw && (
                <p className="text-sm text-gray-600">
                  {order.shippingAddress.raw}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-primary">
                Billing Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              {getAddressValue(order.billingAddress.name) && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  <p className="font-medium">
                    {getAddressValue(order.billingAddress.name)}
                  </p>
                </div>
              )}
              {getAddressValue(order.billingAddress.address1) && (
                <p className="ml-6">
                  {getAddressValue(order.billingAddress.address1)}
                </p>
              )}
              {getAddressValue(order.billingAddress.address2) && (
                <p className="ml-6">
                  {getAddressValue(order.billingAddress.address2)}
                </p>
              )}
              {(getAddressValue(order.billingAddress.city) ||
                getAddressValue(order.billingAddress.state) ||
                getAddressValue(order.billingAddress.zip)) && (
                <p className="ml-6">
                  {getAddressValue(order.billingAddress.city)}
                  {getAddressValue(order.billingAddress.city) &&
                  getAddressValue(order.billingAddress.state)
                    ? ", "
                    : ""}
                  {getAddressValue(order.billingAddress.state)}{" "}
                  {getAddressValue(order.billingAddress.zip)}
                </p>
              )}
              {getAddressValue(order.billingAddress.country) && (
                <p className="ml-6">
                  {getAddressValue(order.billingAddress.country)}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-primary">
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-gray-700">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-secondary" />
                <p className="font-medium">
                  {order.paymentMethod || "Payment method not available"}
                </p>
              </div>
              {order.paymentStatus && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge
                    className={
                      order.paymentStatus === "paid"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }
                  >
                    {order.paymentStatus}
                  </Badge>
                </div>
              )}
              {order.paymentTransactionId && (
                <p className="text-sm text-gray-600 break-all">
                  Transaction ID: {order.paymentTransactionId}
                </p>
              )}
              <p className="text-sm text-gray-600">Charged on {order.date}</p>
            </CardContent>
          </Card>

          {/* Order Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Order Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(order.status === "Delivered" || order.status === "Shipped") && (
                <Dialog
                  open={reorderDialogOpen}
                  onOpenChange={setReorderDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      className="w-full bg-primary hover:bg-primary/90 text-white"
                      disabled={isProcessing}
                    >
                      <RefreshCcw className="w-4 h-4 mr-2" /> Reorder Items
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reorder Items</DialogTitle>
                      <DialogDescription>
                        Create a new order with the same items from this order.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <p className="text-sm text-gray-600">
                        This will create a new order with the same items and
                        shipping address.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setReorderDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleReorder} disabled={isProcessing}>
                        {isProcessing ? "Processing..." : "Confirm Reorder"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {(order.status === "Delivered" || order.status === "Shipped") && (
                <Dialog
                  open={returnDialogOpen}
                  onOpenChange={setReturnDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white bg-transparent"
                      disabled={isProcessing}
                    >
                      <Box className="w-4 h-4 mr-2" /> Request Return
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Request Return</DialogTitle>
                      <DialogDescription>
                        Please provide a reason and upload product images for
                        your return request.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="returnReason">Return Reason *</Label>
                        <Textarea
                          id="returnReason"
                          placeholder="Please explain why you want to return this order..."
                          value={returnReason}
                          onChange={(e) => setReturnReason(e.target.value)}
                          rows={4}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Select Items to Return (Optional)</Label>
                        <p className="text-sm text-gray-600 mb-2">
                          If not selected, all items will be returned.
                        </p>
                        <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                          {order.products.map((item) => {
                            const isSelected = selectedReturnItems.find(
                              (i) => i.product_id === item.product_id,
                            );
                            return (
                              <div
                                key={item.product_id}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                                  isSelected
                                    ? "bg-primary/10 border border-primary"
                                    : "border"
                                }`}
                                onClick={() => toggleReturnItem(item)}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!isSelected}
                                  onChange={() => toggleReturnItem(item)}
                                  className="cursor-pointer"
                                />
                                <span className="flex-1 text-sm">
                                  {item.product_name} (Qty: {item.quantity})
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="returnImages">
                          Product Images * (Max 10)
                        </Label>
                        <p className="text-sm text-gray-600 mb-2">
                          Upload at least one image of the product(s) you wish
                          to return.
                        </p>
                        <Input
                          id="returnImages"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          disabled={returnImages.length >= 10}
                          className="mt-1"
                        />
                        {returnImages.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 mt-4">
                            {returnImages.map((file, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-24 object-cover rounded border"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-1 right-1 h-6 w-6"
                                  onClick={() => removeImage(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setReturnDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleReturnRequest}
                        disabled={
                          isProcessing ||
                          !returnReason.trim() ||
                          returnImages.length === 0
                        }
                      >
                        {isProcessing
                          ? "Submitting..."
                          : "Submit Return Request"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}

              {(order.status === "Pending" ||
                order.status === "Processing") && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleCancelOrder}
                  disabled={isProcessing}
                >
                  Cancel Order
                </Button>
              )}

              <Button
                variant="ghost"
                className="w-full text-primary hover:text-secondary"
              >
                <HelpCircle className="w-4 h-4 mr-2" /> Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
